import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import LoginRequiredConfirm from '@/components/feature/LoginRequiredConfirm';
import { dispatchToast } from '@/components/ui/GlobalToast';
import { recordShare } from '@/api/interaction';
import { getPublicTools, type ToolDto } from '@/api/tool';
import { useAuth } from '@/hooks/useAuth';
import { useCollect } from '@/hooks/useCollect';
import { usePageView } from '@/hooks/usePageView';
import { useHistory } from '@/hooks/useHistory';
import { useAttachmentDownload } from '@/hooks/useAttachmentDownload';
import { useLike } from '@/hooks/useLike';
import ToolAttachmentPanel from '@/pages/tools/detail/components/AttachmentPanel';
import { getPublicPointsOverview } from '@/api/points';
import { fetchDictBatch } from '@/api/dict';
import { usePublicSubPageContent } from '@/hooks/usePublicSubPageContent';

function readText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => readText(item))
    .filter((item): item is string => Boolean(item));
}

function clampStars(value: unknown): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(5, Math.round(value)));
}

function isValidLinkUrl(value: unknown): value is string {
  const text = readText(value);
  if (!text) return false;
  return /^https?:\/\//i.test(text) || text.startsWith('/');
}

function resolveDownloadsText(tool: ToolDto | undefined): string | null {
  if (!tool) return null;
  const direct = readText(tool.downloads);
  if (direct) return direct;
  if (typeof tool.downloadCount === 'number' && Number.isFinite(tool.downloadCount)) {
    return String(tool.downloadCount);
  }
  return null;
}

function PdfPreviewModal({
  url,
  name,
  onClose,
}: {
  url: string;
  name: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
              <i className="ri-file-pdf-2-line text-red-500"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">{name}</h3>
              <p className="text-xs text-gray-400">PDF 在线预览</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
              <i className="ri-close-line text-gray-500"></i>
            </button>
          </div>
        </div>
        <div className="flex-1 bg-gray-100 relative">
          {!url ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <i className="ri-file-pdf-2-line text-5xl mb-3 text-red-300"></i>
              <p className="text-sm font-medium text-gray-600 mb-1">{name}</p>
              <p className="text-xs">该附件未配置可预览地址</p>
            </div>
          ) : (
            <object data={url} type="application/pdf" className="w-full h-full border-0" aria-label={name}>
              <iframe src={`${url}#toolbar=0&navpanes=0`} className="w-full h-full border-0" title={name} />
            </object>
          )}
        </div>
      </div>
    </div>
  );
}

interface CodeBlockProps {
  code: string;
  label?: string;
}

function CodeBlock({ code, label }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = code;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [code]);

  return (
    <div className="rounded-xl overflow-hidden border border-gray-800">
      <div className="flex items-center justify-between px-4 py-2 bg-[#1e293b]">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400/60"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-400/60"></div>
            <div className="w-3 h-3 rounded-full bg-green-400/60"></div>
          </div>
          {label && <span className="text-white/30 text-xs ml-2">{label}</span>}
        </div>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
            copied
              ? 'bg-[#10b981]/20 text-[#10b981]'
              : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'
          }`}
        >
          <i className={`${copied ? 'ri-checkbox-circle-line' : 'ri-clipboard-line'} text-sm`}></i>
          {copied ? '已复制！' : '复制'}
        </button>
      </div>
      <div className="bg-[#0f172a] px-4 py-3 overflow-x-auto">
        <pre className="text-sm font-mono leading-relaxed whitespace-pre-wrap break-all">
          <code>
            {code.split(' ').map((part, i) => {
              if (part.startsWith('docker') || part === 'mkdir' || part === 'cd') {
                return <span key={i} className="text-[#38bdf8]">{part} </span>;
              }
              if (part.startsWith('-')) {
                return <span key={i} className="text-[#f97316]">{part} </span>;
              }
              if (part.startsWith('"') || part.startsWith("'")) {
                return <span key={i} className="text-[#10b981]">{part} </span>;
              }
              return <span key={i} className="text-[#e2e8f0]">{part} </span>;
            })}
          </code>
        </pre>
      </div>
    </div>
  );
}

export default function ToolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [allTools, setAllTools] = useState<ToolDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { content: subPageContent, error: subPageError } = usePublicSubPageContent();
  const [dictCategories, setDictCategories] = useState<{ label: string; value: string }[]>([]);
  const [dictPlatforms, setDictPlatforms] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    fetchDictBatch(['tool_category', 'tool_platform'])
      .then((dicts) => {
        if (dicts['tool_category']) {
          setDictCategories(dicts['tool_category'].map((x) => ({ label: x.label, value: x.value })));
        }
        if (dicts['tool_platform']) {
          setDictPlatforms(dicts['tool_platform'].map((x) => ({ label: x.label, value: x.value })));
        }
      })
      .catch((err) => console.error('Failed to load tool categories and platforms in detail page:', err));
  }, []);

  const getCategoryLabel = useCallback((cat: string) => {
    if (!cat) return '无';
    if (cat === '全部') return '全部';
    const found = dictCategories.find((c) => c.value === cat);
    return found ? found.label : cat;
  }, [dictCategories]);

  const getPlatformLabel = useCallback((plat: string) => {
    const found = dictPlatforms.find((p) => p.value.toLowerCase() === plat.toLowerCase());
    return found ? found.label : plat;
  }, [dictPlatforms]);

  const refreshTools = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await getPublicTools();
      const list = Array.isArray(res?.list) ? res.list : [];
      setAllTools(list);
    } catch (err) {
      setAllTools([]);
      setLoadError(err instanceof Error ? err.message : '获取工具详情失败');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshTools();
    const handleRefresh = () => void refreshTools();
    window.addEventListener('wanxiang_tools_updated', handleRefresh);
    return () => window.removeEventListener('wanxiang_tools_updated', handleRefresh);
  }, [refreshTools]);

  const tool = useMemo(
    () => allTools.find((item) => String(item.id) === String(id ?? '')),
    [allTools, id],
  );

  const toolId = tool?.id ? String(tool.id) : '';
  const toolName = readText(tool?.name) || '';
  const category = readText(tool?.category);
  const version = readText(tool?.version);
  const size = readText(tool?.size);
  const longDesc = readText((tool as any)?.detail?.intro) || readText(tool?.longDesc);
  const requirements = readText(tool?.requirements);
  const downloadsText = resolveDownloadsText(tool);
  const stars = clampStars(tool?.stars);
  const headerGradientFrom = readText(subPageContent.toolDetail.headerGradientFrom) || '#1a0a00';
  const headerGradientTo = readText(subPageContent.toolDetail.headerGradientTo) || '#7c2d12';

  const tags = useMemo(() => readStringArray(tool?.tags), [tool?.tags]);
  const platforms = useMemo(() => readStringArray(tool?.platform), [tool?.platform]);
  const detailImages = useMemo(() => readStringArray((tool as any)?.detailImages), [tool]);
  const installSteps = useMemo(() => readStringArray((tool as any)?.detail?.install), [tool]);
  const usageItems = useMemo(() => readStringArray((tool as any)?.detail?.usage), [tool]);
  const faqItems = useMemo(
    () =>
      Array.isArray((tool as any)?.detail?.faq)
        ? (tool as any).detail.faq
            .map((item: any) => {
              const q = readText(item?.q);
              const a = readText(item?.a);
              if (!q || !a) return null;
              return { q, a };
            })
            .filter((item: { q: string; a: string } | null): item is { q: string; a: string } => Boolean(item))
        : [],
    [tool],
  );

  const links = useMemo(
    () =>
      (tool?.links ?? [])
        .map((link, idx) => {
          const title = readText((link as any)?.label) || readText((link as any)?.title) || readText((link as any)?.type);
          const url = readText((link as any)?.url);
          if (!title || !url || !isValidLinkUrl(url)) return null;
          return {
            key: `${idx}-${url}`,
            title,
            url,
            type: (link as any)?.type || 'other',
          };
        })
        .filter((item): item is { key: string; title: string; url: string; type: string } => Boolean(item)),
    [tool?.links],
  );

  const attachments = useMemo(
    () =>
      (tool?.attachments ?? []).filter(
        (item) => Boolean(readText((item as any)?.name)),
      ) as any[],
    [tool?.attachments],
  );
  const hasAttachments = attachments.length > 0;

  const commands = useMemo(
    () =>
      Array.isArray((tool as any)?.commands)
        ? (tool as any).commands
            .map((item: any) => {
              const label = readText(item?.label);
              const cmd = readText(item?.cmd);
              if (!cmd) return null;
              return { label: label || '命令', cmd };
            })
            .filter((item: { label: string; cmd: string } | null): item is { label: string; cmd: string } => Boolean(item))
        : [],
    [tool],
  );

  const { isLoggedIn } = useAuth();
  const { isCollected, toggle } = useCollect();
  const { record } = useHistory();
  const viewCount = usePageView('tool', toolId);

  const [showLoginConfirm, setShowLoginConfirm] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<{ previewUrl: string; name: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'intro' | 'install' | 'usage' | 'faq'>('intro');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const favored = toolId ? isCollected(toolId, 'tool') : false;
  const { likeCount, isLiked, toggle: toggleLike, featureEnabled: likeEnabled } = useLike(
    'tool',
    toolId || 'tool-detail',
    toolName || '工具',
  );

  const [shareEnabled, setShareEnabled] = useState(false);
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const overview = await getPublicPointsOverview(1);
        if (!active) return;
        if (typeof overview.features?.share !== 'boolean') {
          throw new Error('分享功能开关响应格式错误');
        }
        setShareEnabled(overview.features.share);
      } catch (err) {
        console.error('Failed to sync share feature switch in tool detail:', err);
        if (!active) return;
        setShareEnabled(false);
      }
    };
    const handleRefresh = () => { void refresh(); };
    void refresh();
    window.addEventListener('wanxiang_feature_updated', handleRefresh);
    window.addEventListener('wanxiang_settings_updated', handleRefresh);
    return () => {
      active = false;
      window.removeEventListener('wanxiang_feature_updated', handleRefresh);
      window.removeEventListener('wanxiang_settings_updated', handleRefresh);
    };
  }, []);

  const closePdfPreview = useCallback(() => {
    setPdfPreview(null);
  }, []);

  const {
    toast: attachToast,
    showLoginHint,
    dismissLoginHint,
    checkAndDownload,
    getAttachmentStatus,
  } = useAttachmentDownload('tool', toolId || 'tool-detail');

  const handleLike = useCallback(async () => {
    if (!toolId) return;
    if (!isLoggedIn) {
      setShowLoginConfirm(true);
      return;
    }
    const result = await toggleLike();
    if (result.success) {
      dispatchToast(result.message.includes('取消') ? 'info' : 'success', result.message);
      return;
    }
    dispatchToast('error', result.message || '点赞失败，请稍后重试');
  }, [isLoggedIn, toggleLike, toolId]);

  const handleShare = useCallback(async () => {
    if (!toolId) return;
    const url = window.location.href;
    let copied = false;
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      copied = document.execCommand('copy');
      document.body.removeChild(el);
    }
    if (!copied) {
      dispatchToast('error', '链接复制失败，请稍后重试');
      return;
    }

    if (isLoggedIn) {
      try {
        const res = await recordShare({
          targetType: 'tool',
          targetId: toolId,
          targetTitle: toolName || '工具',
          channel: 'copy_link',
        });
        if (res.rewarded) {
          window.dispatchEvent(new CustomEvent('wanxiang_points_updated'));
        }
        dispatchToast(res.rewarded ? 'success' : 'info', res.message);
      } catch (err) {
        dispatchToast('error', err instanceof Error ? err.message : '分享奖励发放失败');
      }
    } else {
      dispatchToast('success', '链接已复制！');
    }
  }, [isLoggedIn, toolId, toolName]);

  const handleFavor = useCallback(async () => {
    if (!toolId) return;
    if (!isLoggedIn) {
      setShowLoginConfirm(true);
      return;
    }
    const result = await toggle(toolId, 'tool');
    if (!result.success) {
      dispatchToast('error', result.message || '收藏操作失败，请稍后重试');
      return;
    }
    dispatchToast(result.isCollected ? 'success' : 'info', result.message);
  }, [isLoggedIn, toggle, toolId]);

  const scrollToAttachments = useCallback(() => {
    if (!hasAttachments) {
      dispatchToast('info', '该工具暂无可下载附件');
      return;
    }
    document.getElementById('tool-attachments')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hasAttachments]);

  useEffect(() => {
    if (!toolId || !toolName) return;
    void record({
      id: toolId,
      type: 'tool',
      title: toolName,
      category: category || '',
    });
  }, [category, record, toolId, toolName]);

  const tabs = [
    { key: 'intro', label: '工具介绍', icon: 'ri-information-line' },
    { key: 'install', label: '安装方法', icon: 'ri-install-line' },
    { key: 'usage', label: '使用方法', icon: 'ri-play-circle-line' },
    { key: 'faq', label: '常见问题', icon: 'ri-question-answer-line' },
  ] as const;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 pt-28 pb-20">
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-500">
            正在加载工具详情...
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 pt-28 pb-20">
          <div className="bg-white rounded-2xl border border-red-100 p-10 text-center">
            <p className="text-red-500 mb-4">工具数据加载失败：{loadError}</p>
            <button
              onClick={() => void refreshTools()}
              className="px-4 py-2 bg-[#f97316] text-white text-sm rounded-lg hover:bg-[#ea6c0a] transition-colors cursor-pointer"
            >
              重试
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 pt-28 pb-20">
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <p className="text-gray-700 mb-2">未找到该工具</p>
            <p className="text-sm text-gray-400 mb-6">该工具可能已下架或不存在。</p>
            <Link
              to="/tools"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#f97316] text-white text-sm rounded-lg hover:bg-[#ea6c0a] transition-colors"
            >
              返回工具列表
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {pdfPreview && (
        <PdfPreviewModal
          url={pdfPreview.previewUrl}
          name={pdfPreview.name}
          onClose={closePdfPreview}
        />
      )}

      {showLoginHint && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[300] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-7 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 flex items-center justify-center bg-[#f97316]/10 rounded-2xl mx-auto mb-4">
              <i className="ri-lock-password-line text-[#f97316] text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 text-base mb-2">需要登录才能下载</h3>
            <p className="text-gray-500 text-sm mb-5 leading-relaxed">该附件需要登录后才能下载，登录后还可以收藏工具、解锁灵石权益。</p>
            <div className="flex gap-3">
              <button onClick={dismissLoginHint} className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap">稍后再说</button>
              <button
                onClick={() => {
                  dismissLoginHint();
                  navigate('/login', { state: { from: location.pathname } });
                }}
                className="flex-1 py-2.5 bg-[#f97316] text-white text-sm font-medium rounded-xl hover:bg-[#ea6c0a] cursor-pointer whitespace-nowrap"
              >立即登录</button>
            </div>
          </div>
        </div>
      )}

      <LoginRequiredConfirm
        open={showLoginConfirm}
        title="需要登录后访问"
        description="该功能需要登录后才能继续，是否前往登录页面？"
        onConfirm={() => {
          setShowLoginConfirm(false);
          navigate('/login', { state: { from: location.pathname } });
        }}
        onCancel={() => setShowLoginConfirm(false)}
      />

      {attachToast && (
        <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[200] px-5 py-2.5 text-white text-sm rounded-full flex items-center gap-2 ${
          attachToast.type === 'success' ? 'bg-[#10b981]' : attachToast.type === 'error' ? 'bg-red-500' : 'bg-gray-800/90'
        }`}>
          <i className={`${attachToast.type === 'success' ? 'ri-checkbox-circle-line' : attachToast.type === 'error' ? 'ri-error-warning-line' : 'ri-information-line'} text-base`}></i>
          {attachToast.msg}
        </div>
      )}

      <Navbar />

      <div
        className="pt-28 pb-12 px-6"
        style={{ background: `linear-gradient(135deg, ${headerGradientFrom}, ${headerGradientTo})` }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/tools" className="text-white/60 text-sm hover:text-white cursor-pointer">工具下载</Link>
            <i className="ri-arrow-right-s-line text-white/40"></i>
            <span className="text-white/80 text-sm">{toolName || '无'}</span>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-2xl">
              <i className="ri-tools-fill text-white text-2xl"></i>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">{toolName || '无'}</h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-white/60">
                <span className="px-2 py-0.5 bg-white/10 rounded-md">{getCategoryLabel(category || '')}</span>
                <span>{version ? `v${version}` : '无版本'}</span>
                <span>{size || '无大小'}</span>
                <span><i className="ri-download-line mr-1"></i>{downloadsText ? `${downloadsText} 次下载` : '无下载数据'}</span>
              </div>
            </div>
            <div className="sm:ml-auto flex items-center gap-3">
              {likeEnabled && (
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-1.5 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    isLiked
                      ? 'bg-[#f97316]/30 border-[#f97316]/40 text-white'
                      : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                  }`}
                  title="点赞此工具"
                >
                  <i className={`${isLiked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-base`}></i>
                  {likeCount > 0 ? likeCount : ''} {isLiked ? '已点赞' : '点赞'}
                </button>
              )}
              {shareEnabled && (
                <button
                  onClick={handleShare}
                  className="flex items-center gap-1.5 px-4 py-3 rounded-xl text-sm font-medium bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
                  title="复制链接分享"
                >
                  <i className="ri-share-line text-base"></i>
                  分享
                </button>
              )}
              <button
                onClick={handleFavor}
                className={`flex items-center gap-1.5 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                  favored
                    ? 'bg-red-500/20 border-red-400/40 text-red-300'
                    : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                }`}
              >
                <i className={`${favored ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
                {favored ? '已收藏' : '收藏'}
              </button>
              {hasAttachments ? (
                <button
                  type="button"
                  onClick={scrollToAttachments}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#f97316] text-white font-semibold rounded-xl hover:bg-[#ea6c0a] transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-download-2-line"></i>
                  查看附件
                </button>
              ) : (
                <span className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white/70 font-semibold rounded-xl cursor-not-allowed whitespace-nowrap">
                  <i className="ri-download-2-line"></i>
                  暂无附件
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-5">
            <div className="flex gap-1 bg-white border border-gray-100 rounded-2xl p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab.key ? 'bg-[#f97316] text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className={tab.icon}></i>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              {activeTab === 'intro' && (
                <div>
                  <h2 className="font-bold text-gray-900 text-lg mb-4">工具介绍</h2>
                  <p className="text-gray-600 leading-relaxed mb-6">{longDesc || '无'}</p>
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-[#f97316]/10 text-[#f97316] text-sm rounded-full">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'install' && (
                <div>
                  <h2 className="font-bold text-gray-900 text-lg mb-5">安装步骤</h2>
                  {installSteps.length > 0 ? (
                    <ol className="space-y-5">
                      {installSteps.map((step, i) => (
                        <li key={`${i}-${step}`} className="flex gap-4">
                          <div className="w-7 h-7 flex items-center justify-center bg-[#f97316] text-white text-sm font-bold rounded-full shrink-0 mt-0.5">
                            {i + 1}
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-600 leading-relaxed mb-2">{step}</p>
                            {(step.includes('docker') || step.includes('运行') || step.includes('执行')) && commands[i] && (
                              <CodeBlock code={commands[i].cmd} label={commands[i].label} />
                            )}
                          </div>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-gray-400">暂无安装步骤</p>
                  )}
                </div>
              )}

              {activeTab === 'usage' && (
                <div>
                  <h2 className="font-bold text-gray-900 text-lg mb-4">使用方法</h2>
                  {usageItems.length > 0 ? (
                    <ul className="space-y-3">
                      {usageItems.map((item, i) => (
                        <li key={`${i}-${item}`} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                            <i className="ri-checkbox-circle-fill text-[#10b981] text-lg"></i>
                          </div>
                          <p className="text-gray-600">{item}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-400">暂无使用说明</p>
                  )}
                </div>
              )}

              {activeTab === 'faq' && (
                <div>
                  <h2 className="font-bold text-gray-900 text-lg mb-4">常见问题</h2>
                  {faqItems.length > 0 ? (
                    <div className="space-y-3">
                      {faqItems.map((item, i) => (
                        <div key={`${i}-${item.q}`} className="border border-gray-100 rounded-xl overflow-hidden">
                          <button
                            onClick={() => setOpenFaq(openFaq === i ? null : i)}
                            className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <span className="w-5 h-5 flex items-center justify-center bg-[#f97316] text-white text-xs font-bold rounded-full shrink-0">Q</span>
                            <p className="font-semibold text-gray-900 text-sm flex-1">{item.q}</p>
                            {openFaq === i
                              ? <i className="ri-arrow-up-s-line text-gray-400 shrink-0"></i>
                              : <i className="ri-arrow-down-s-line text-gray-400 shrink-0"></i>
                            }
                          </button>
                          {openFaq === i && (
                            <div className="px-4 pb-4 flex items-start gap-3">
                              <span className="w-5 h-5 flex items-center justify-center bg-[#0ea5e9] text-white text-xs font-bold rounded-full shrink-0 mt-0.5">A</span>
                              <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">暂无常见问题</p>
                  )}
                </div>
              )}
            </div>

            {commands.length > 0 && (tool as any).showCommands !== false && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#0f172a] rounded-lg">
                    <i className="ri-terminal-line text-[#38bdf8] text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm">常用命令</h3>
                    <p className="text-xs text-gray-400">点击右上角按钮一键复制</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {commands.map((item) => (
                    <CodeBlock key={`${item.label}-${item.cmd}`} code={item.cmd} label={item.label} />
                  ))}
                </div>
              </div>
            )}

            {detailImages.length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-900 text-sm mb-3">详情图片</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {detailImages.map((img, idx) => (
                    <div key={`${idx}-${img}`} className="rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                      <img src={img} alt="工具详情图" className="w-full h-52 object-cover object-top" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-5">
            <div className="bg-white rounded-2xl p-5 border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-4">工具信息</h3>
              <div className="space-y-3 text-sm">
                {[
                  { label: '分类', value: getCategoryLabel(category || '') },
                  { label: '版本', value: version ? `v${version}` : '无' },
                  { label: '大小', value: size || '无' },
                  { label: '下载量', value: downloadsText || '无' },
                  { label: '浏览次数', value: viewCount === null ? '—' : viewCount.toLocaleString() },
                ].map((item) => (
                  <div key={item.label} className="flex justify-between items-center py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-gray-400">{item.label}</span>
                    <span className="font-medium text-gray-900">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-5 border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-3">支持平台</h3>
              {platforms.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {platforms.map((p) => {
                    const icons: Record<string, string> = {
                      Windows: 'ri-windows-line',
                      windows: 'ri-windows-line',
                      macOS: 'ri-apple-line',
                      macos: 'ri-apple-line',
                      Linux: 'ri-terminal-line',
                      linux: 'ri-terminal-line',
                      Docker: 'ri-box-3-line',
                      docker: 'ri-box-3-line',
                      Web: 'ri-global-line',
                      web: 'ri-global-line',
                    };
                    return (
                      <div key={p} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 text-xs rounded-lg">
                        <i className={`${icons[p] || icons[p.toLowerCase()] || 'ri-computer-line'} text-sm`}></i>
                        {getPlatformLabel(p)}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400">无</p>
              )}
            </div>

            <div className="bg-white rounded-2xl p-5 border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-3">社区评分</h3>
              {stars > 0 ? (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className={`ri-star-fill text-lg ${i < stars ? 'text-[#f97316]' : 'text-gray-200'}`}></i>
                      ))}
                    </div>
                    <span className="font-bold text-gray-900">{stars}.0</span>
                  </div>
                  <p className="text-xs text-gray-400">{downloadsText || '无'} 次下载</p>
                </>
              ) : (
                <p className="text-sm text-gray-400">无</p>
              )}
            </div>

            {requirements && (
              <div className="bg-white rounded-2xl p-5 border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3">系统要求</h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{requirements}</p>
              </div>
            )}

            <div id="tool-attachments" className="scroll-mt-24">
              <ToolAttachmentPanel
                attachments={attachments}
                checkAndDownload={checkAndDownload}
                getAttachmentStatus={getAttachmentStatus}
                onRequireLogin={() => {}}
                onPdfPreview={(previewUrl, name) => setPdfPreview({ previewUrl, name })}
                accentColor="#f97316"
              />
            </div>

            {links.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-gray-100">
                <h3 className="font-bold text-gray-900 text-sm mb-3">相关链接</h3>
                <ul className="space-y-1">
                  {links.map((link) => {
                    const typeIconMap: Record<string, string> = {
                      github: 'ri-github-line text-gray-700',
                      docs: 'ri-book-open-line text-[#0ea5e9]',
                      download: 'ri-download-line text-[#10b981]',
                      video: 'ri-video-line text-red-500',
                      other: 'ri-link text-gray-400',
                    };
                    const iconClass = typeIconMap[link.type] || 'ri-link text-gray-400';
                    return (
                      <li key={link.key}>
                        <a href={link.url} target="_blank" rel="nofollow noreferrer"
                          className="flex items-center gap-2 px-2 py-2 rounded-xl hover:bg-gray-50 transition-colors group cursor-pointer">
                          <div className="w-7 h-7 flex items-center justify-center shrink-0">
                            <i className={`${iconClass} text-base`}></i>
                          </div>
                          <span className="flex-1 text-xs text-gray-700 group-hover:text-[#f97316] transition-colors truncate">{link.title}</span>
                          <i className="ri-external-link-line text-gray-300 group-hover:text-gray-400 text-xs shrink-0"></i>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div
              className="rounded-2xl p-5 text-white"
              style={{ background: `linear-gradient(135deg, ${headerGradientFrom}, ${headerGradientTo})` }}
            >
              <h3 className="font-bold mb-2 text-sm">{subPageContent.toolDetail.helpCard.title || '无'}</h3>
              <p className="text-white/80 text-sm mb-4 leading-relaxed">{subPageContent.toolDetail.helpCard.desc || '无'}</p>
              {subPageError && <p className="text-xs text-white/70 mb-3">区块内容加载失败：{subPageError}</p>}
              <button
                onClick={() => {}}
                className="block w-full text-center py-2 bg-white font-semibold text-sm rounded-xl hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap"
                style={{ color: headerGradientTo }}
              >
                {subPageContent.toolDetail.helpCard.btnText || '无'}
              </button>
            </div>

            {hasAttachments ? (
              <button
                type="button"
                onClick={scrollToAttachments}
                className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#f97316] text-white font-semibold rounded-xl hover:bg-[#ea6c0a] transition-all cursor-pointer"
              >
                <i className="ri-download-2-line text-lg"></i>
                查看附件
              </button>
            ) : (
              <span className="flex items-center justify-center gap-2 w-full py-3.5 bg-gray-100 text-gray-400 font-semibold rounded-xl cursor-not-allowed">
                <i className="ri-download-2-line text-lg"></i>
                暂无附件
              </span>
            )}

            <Link
              to="/tools"
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 cursor-pointer justify-center"
            >
              <i className="ri-arrow-left-line"></i>
              返回工具列表
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
